import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  AssignmentService,
  AssignmentMontage,
  Assignment,
  ServiceRecord,
  MaintenanceRecord,
  AssignmentServiceDossier,
} from '@api/models/assignment.model';
import { AssignmentsService } from '@api/services/assignments.service';
import { CardComponent } from '@ui/card/card.component';
import { TimelineItem } from '@ui/timeline/timeline.component';
import { TimelineComponent } from '@ui/timeline/timeline.component';
import { ServiceModalComponent } from '../service-modal/service-modal.component';
import { ModalService } from '@ui/modal/modal.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-assignments-timeline',
  standalone: true,
  imports: [CommonModule, CardComponent, TimelineComponent],
  template: `<ui-card [title]="'Service en Onderhoud'" [loading]="loading()">
    <div class="space-y-4 h-full overflow-y-auto">
      <ui-timeline [items]="timelineRecords()" [showTime]="false"></ui-timeline>
    </div>
  </ui-card>`,
})
export class AssignmentsTimelineComponent {
  private modalService = inject(ModalService);
  assignmentsService = inject(AssignmentsService);
  assignment = input.required<Assignment | null>();
  serviceDossier = signal<AssignmentServiceDossier | null>(null);
  loading = signal<boolean>(false);

  constructor() {
    effect(
      () => {
        let assignmentId = this.assignment()?.id;
        if (assignmentId) {
          this.getService(assignmentId);
        }
      },
      { allowSignalWrites: true }
    );
  }

  timelineRecords = computed(() => {
    const records: TimelineItem[] = [];

    // Add maintenance records
    this.serviceDossier()?.maintenance_records?.forEach((record) => {
      records.push({
        title: 'Onderhoud',
        date: record.bezoekdatum_monteur || record.instuur_datum,
        content: 'Gepland onderhoud',
        badges: [
          {
            label: 'Uitgevoerd',
            icon: 'check',
            color: 'bg-green-100 text-green-800',
          },
        ],
        onClick: () => {
          this.openServiceModal(record, true);
        },
      });
    });

    this.serviceDossier()?.service_records?.forEach((record) => {

      let item: TimelineItem = {
        title: record.reden_service,
        date: record.bezoekdatum_monteur || record.instuur_datum,
        content: record.oplossing || '',
        onClick: () => {
          this.openServiceModal(record);
        },
      };

      item.badges = [];
      if (record.attachments?.length) {
        item.badges.push({
          label: record.attachments?.length?.toString() || '0',
          icon: 'document-text',
          color: 'bg-blue-100 text-blue-800',
        });
      }

      if (record.opgelost) {
        item.badges.push({
          label: 'Opgelost',
          icon: 'check',
          color: 'bg-green-100 text-green-800',
        });
      }
      records.push(item);
    });
    return records;
  });

  getService(assignmentId: string) {
    this.loading.set(true);
    this.assignmentsService
      .getAssignmentServiceDossier(assignmentId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((service) => {
        this.serviceDossier.set(service);
      });
  }

  openServiceModal(record: ServiceRecord | MaintenanceRecord, isMaintenance: boolean = false) {
    let modal = this.modalService.open(ServiceModalComponent, {
      data: {
        item: record,
        assignmentId: this.assignment()?.id,
        isMaintenance: isMaintenance,
      },
    });
  }
}
